import { useCallback, useEffect, useMemo, useState } from "react";

import { useCart } from "@shopify/hydrogen-react";
import {
  type CartLine,
  type Maybe,
} from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";
import { type PartialDeep } from "type-fest";

import cartMetafieldsSetMutation from "@graphql/cart-metafields.mutation.graphql";
import { storefrontAPIFetcher } from "@react/hooks/fetchers";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  STOREFRONT_API_URL,
  STOREFRONT_CART_ATTRIBUTES,
} from "@tablescape/constants";
import {
  type CartSectionSettings,
  type CartMerchandiseLine,
  type CartUserSelectedGiftsWithPurchase,
} from "@tablescape/state";
import {
  findRelatedCartProductGiftWithPurchaseLine,
  findRelatedCartSpendingGiftWithPurchaseLine,
  hasCartLineAttribute,
} from "@tablescape/utilities";

type CartMetafieldsSetConnection = PartialDeep<
  {
    data: {
      cartMetafieldsSet: {
        metafields: {
          key: string;
          value: string;
        }[];
        userErrors: [];
      };
    };
  },
  { recurseIntoArrays: true }
>;

export const useCartMetafieldsSetParams = [
  STOREFRONT_API_URL,
  cartMetafieldsSetMutation,
];

const USER_SELECTED_GIFT_WITH_PURCHASE_KEY = "userSelectedGiftsWithPurchase";

export function useRelatedGiftWithPurchaseItems() {
  const {
    meta: {
      localization: { country },
    },
  } = useSectionSettings<CartSectionSettings>();

  const { id: cartId, lines, userSelectedGiftsWithPurchase } = useCart();

  const [cartMetafieldsSetInput, setCartMetafieldsSetInput] = useState<
    Maybe<CartUserSelectedGiftsWithPurchase> | undefined
  >(undefined);

  useEffect(() => {
    if (!userSelectedGiftsWithPurchase?.value) {
      return undefined;
    }

    const existingRelatedItems = JSON.parse(
      userSelectedGiftsWithPurchase.value
    ) satisfies CartUserSelectedGiftsWithPurchase;
    setCartMetafieldsSetInput(existingRelatedItems);
  }, [userSelectedGiftsWithPurchase?.value]);

  const { data, isLoading } = useSWR<CartMetafieldsSetConnection>(
    () =>
      cartMetafieldsSetInput !== undefined && cartId
        ? [
            ...useCartMetafieldsSetParams,
            {
              metafields: [
                {
                  key: USER_SELECTED_GIFT_WITH_PURCHASE_KEY,
                  ownerId: cartId,
                  type: "json",
                  value: JSON.stringify(cartMetafieldsSetInput),
                },
              ],
              country,
            },
          ]
        : null,
    storefrontAPIFetcher
  );

  const newlyAddedRelatedItems = useMemo<
    CartUserSelectedGiftsWithPurchase | undefined
  >(() => {
    if (isLoading) {
      return undefined;
    }

    const newValue = data?.data?.cartMetafieldsSet?.metafields?.find(
      (item) => item?.key === USER_SELECTED_GIFT_WITH_PURCHASE_KEY
    )?.value;

    if (!newValue) {
      return newValue;
    }

    return JSON.parse(newValue) satisfies CartUserSelectedGiftsWithPurchase;
  }, [data?.data?.cartMetafieldsSet?.metafields, isLoading]);

  const derivedRelatedItems = useMemo<
    Maybe<CartUserSelectedGiftsWithPurchase> | undefined
  >(() => {
    return newlyAddedRelatedItems ?? cartMetafieldsSetInput;
  }, [cartMetafieldsSetInput, newlyAddedRelatedItems]);

  const clearRelatedGiftWithPurchaseItems = useCallback(() => {
    setCartMetafieldsSetInput(null);
  }, []);

  const relatedCartLines = useMemo<CartMerchandiseLine[] | undefined>(() => {
    const relatedLines = lines
      ?.reduce<typeof lines>((acc, line) => {
        const relatedItems = derivedRelatedItems?.items.find(
          (item) => item.cartLineId === line?.id
        );
        const isCartSpendingGiftItem =
          findRelatedCartSpendingGiftWithPurchaseLine([line]);

        return line
          ? [
              ...acc,
              relatedItems
                ? {
                    ...line,
                    selectedGiftsWithPurchase:
                      relatedItems.selectedGiftWithPurchaseIds
                        .map((id) =>
                          findRelatedCartProductGiftWithPurchaseLine(lines, id)
                        )
                        .filter((line): line is CartLine => !!line),
                  }
                : isCartSpendingGiftItem ||
                  !hasCartLineAttribute(
                    line,
                    STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE
                  )
                ? line
                : undefined,
            ]
          : acc;
      }, [])
      ?.filter((line): line is CartMerchandiseLine => !!line);

    return relatedLines;
  }, [lines, derivedRelatedItems]);

  const setRelatedGiftWithPurchaseItems = useCallback(
    (updatedGiftWithPurchaseItems: CartUserSelectedGiftsWithPurchase) => {
      return setCartMetafieldsSetInput(updatedGiftWithPurchaseItems);
    },
    []
  );

  useEffect(() => {
    if (!cartId || !lines?.length) {
      return;
    }

    // Invalidate if the cart has changed
    if (derivedRelatedItems?.cartId && derivedRelatedItems.cartId !== cartId) {
      clearRelatedGiftWithPurchaseItems();
      return;
    }

    const userSelectedGiftsWithPurchase = {
      cartId,
      items: lines
        .filter(
          (cartLine): cartLine is CartLine =>
            !hasCartLineAttribute(
              cartLine,
              STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE
            )
        )
        .map((cartLine) => ({
          cartLineId: cartLine.id,
          merchandiseId: cartLine.merchandise.id,
          quantity: cartLine.quantity,
          selectedGiftWithPurchaseIds: lines
            ?.filter((giftLine): giftLine is CartLine =>
              hasCartLineAttribute(
                giftLine,
                STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE,
                (value) => {
                  if (value) {
                    try {
                      const parsedValue = JSON.parse(value);
                      return parsedValue?.cartLineId === cartLine.id;
                    } catch {
                      return false;
                    }
                  }

                  return false;
                }
              )
            )
            .map((giftLine) => giftLine.id),
        })),
    };

    if (
      JSON.stringify(cartMetafieldsSetInput) !==
      JSON.stringify(userSelectedGiftsWithPurchase)
    ) {
      setRelatedGiftWithPurchaseItems(userSelectedGiftsWithPurchase);
    }
  }, [
    cartId,
    cartMetafieldsSetInput,
    clearRelatedGiftWithPurchaseItems,
    derivedRelatedItems?.cartId,
    lines,
    setRelatedGiftWithPurchaseItems,
  ]);

  return {
    cartId,
    clearRelatedGiftWithPurchaseItems,
    relatedCartLines,
    relatedCartLinesUpdating: isLoading,
    userSelectedGiftsWithPurchase: cartMetafieldsSetInput,
    setRelatedGiftWithPurchaseItems,
  };
}
